import {
  Role,
  UserStatus,
  type AcceptInvitationRequest,
  type LoginRequest,
  type LoginResponse,
  type SessionUser,
} from '@robu/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { refreshSession } from '../../lib/api-client';
import {
  clearTokens,
  getRefreshToken,
  onSessionEnded,
  setAccessToken,
  setRefreshToken,
} from '../../lib/token-storage';
import { authApi } from './auth-api';
import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context';

/**
 * Session lifecycle.
 *
 * The access token never leaves memory, so a page reload starts with no
 * credential at all: we exchange the persisted refresh token for a fresh access
 * token and then ask the API who we are. `/auth/me` is the single source of
 * truth for role and status — the client never infers permissions from a token
 * payload (TZ §36).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const bootstrapped = useRef(false);

  const applySession = useCallback((response: LoginResponse): SessionUser => {
    setAccessToken(response.accessToken);
    setRefreshToken(response.refreshToken);
    setUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const endSession = useCallback(() => {
    clearTokens();
    setUser(null);
    setStatus('anonymous');
    // Cached player data belongs to the session that just ended.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    // StrictMode mounts effects twice in development, and the refresh token is
    // single-use, so the restore must run exactly once. The ref is what enforces
    // that — deliberately without an accompanying `cancelled` flag. Ignoring the
    // result on unmount would leave the app on the splash screen forever: the
    // remount is suppressed by this very ref, so the cancelled run is the only
    // one that can ever resolve the status.
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const restore = async () => {
      if (!getRefreshToken()) {
        setStatus('anonymous');
        return;
      }

      const token = await refreshSession();
      if (!token) {
        endSession();
        return;
      }

      try {
        setUser(await authApi.me());
        setStatus('authenticated');
      } catch {
        endSession();
      }
    };

    void restore();
  }, [endSession]);

  // The api client emits this when a refresh token stops working mid-session.
  useEffect(() => onSessionEnded(() => endSession()), [endSession]);

  const login = useCallback(
    async (credentials: LoginRequest) => applySession(await authApi.login(credentials)),
    [applySession],
  );

  const acceptInvitation = useCallback(
    async (request: AcceptInvitationRequest) =>
      applySession(await authApi.acceptInvitation(request)),
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // A failed revoke must not trap the user in an authenticated shell.
    } finally {
      endSession();
    }
  }, [endSession]);

  const reloadUser = useCallback(async () => {
    setUser(await authApi.me());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAdmin:
        user?.role === Role.ADMINISTRATOR || user?.role === Role.SUPER_ADMINISTRATOR,
      isSuperAdmin: user?.role === Role.SUPER_ADMINISTRATOR,
      isBlocked: user?.status === UserStatus.BLOCKED || user?.status === UserStatus.INACTIVE,
      login,
      acceptInvitation,
      logout,
      reloadUser,
    }),
    [status, user, login, acceptInvitation, logout, reloadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
